import { useUpdateSegmentationConfigMutation } from 'app/apis/commandApi';
import { useGetSegmentationConfigQuery } from 'app/apis/queryApi';
import { RootState } from 'app/store';
import NavLinkButton from 'common/components/anchors/navlink-button';
import Button from 'common/components/buttons/button';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import { DropDownItemType } from 'common/components/drop-downs/legacy/dropDownItemType';
import Spinner from 'common/components/loading/spinner';
import { selectUserDetails } from 'features/auth/authenticationSlice';
import SegmentationRuleConfigForm from 'features/segmentation-config/segmentation-config-form/segmentation-config-form';
import SegmentationConfigRule from 'features/segmentation-config/segmentation-config-rule/segmentation-config-rule';
import { TabKey } from 'features/segmentation-config/segmentation-config-tab-keys';
import {
  onRuleAccessTypeChanged,
  onRuleAdded,
  onRulesChanged,
  onRuleTargetsChanged,
  onSegmentationConfigNameChanged,
  onSegmentationTypeChanged,
  onSegmentNameChanged,
  onSegmentValueChanged,
  selectConfigName,
  selectConfigNameErrors,
  selectRuleAccessType,
  selectRules,
  selectRuleTargets,
  selectSegmentationCategory,
  selectSegmentationType,
  selectSegmentName,
  selectSegmentNameErrors,
  selectSegmentValue,
  selectTargetTypeOptions
} from 'features/segmentation-config/segmentation-config.slice';
import {
  ConditionalOperator,
  getConditionalOperator,
  getTargetType,
  RuleAccessType,
  RuleTarget,
  SegmentationRuleDto,
  SegmentationType,
  TargetType
} from 'features/segmentation-config/segmentation-config.types';
import TileGridContainer from 'features/tiles/tileGridContainer';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import { onError, onSuccess } from 'features/toast/toast.slice';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

export const SettingsSegmentationConfigEditPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { segmentationConfigId } = useParams();

  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const userDetails = useSelector(selectUserDetails);

  const segmentationCategory = useSelector(selectSegmentationCategory);

  const { data: segmentationConfigResult, isLoading } =
    useGetSegmentationConfigQuery({
      siteId,
      configId: segmentationConfigId ?? '',
      category: segmentationCategory
    });

  useEffect(() => {
    if (segmentationConfigResult && !isLoading) {
      const rules = JSON.parse(segmentationConfigResult.rules!);

      dispatch(
        onSegmentationConfigNameChanged({
          newSegmentationConfigName: segmentationConfigResult.name
        })
      );
      dispatch(
        onSegmentNameChanged({
          newSegmentName: segmentationConfigResult.segmentName
        })
      );
      dispatch(
        onSegmentValueChanged({
          newSegmentValue: segmentationConfigResult.segmentValue
        })
      );
      dispatch(
        onSegmentationTypeChanged({
          newSegmentationType: segmentationConfigResult.segmentationTypeName
        })
      );
      dispatch(
        onRuleAccessTypeChanged({
          newRuleAccessType: segmentationConfigResult.ruleAccessTypeName
        })
      );
      dispatch(
        onRulesChanged({
          rules: rules.map((_: any, ruleIndex: number) => {
            return { id: ruleIndex + 1 };
          })
        })
      );
      const ruleTargets = rules.flatMap((rule: any, ruleIndex: number) =>
        rule.Targets.map((target: any, targetIndex: number) => {
          console.log(target);
          let key = '';
          let value = '';
          const targetType = getTargetType(target.Type);

          if (isRuleTargetKeyValue(targetType)) {
            key = target.Key;
            value = target.Value;
          } else {
            value = target.Value;
          }

          return {
            rule_id: ruleIndex + 1,
            target_id: targetIndex + 1,
            type: targetType,
            key,
            value,
            conditional_operator: getConditionalOperator(
              target.ConditionalOperator
            )
          };
        })
      );
      dispatch(onRuleTargetsChanged({ ruleTargets }));
    }
  }, [segmentationConfigResult, isLoading, dispatch]);

  const allRules = useSelector((state: RootState) => selectRules(state));
  const allTargets = useSelector((state: RootState) =>
    selectRuleTargets(state)
  );

  const addRule = () => {
    dispatch(onRuleAdded());
  };

  const configName = useSelector((state: RootState) => selectConfigName(state));
  const segmentName = useSelector((state: RootState) =>
    selectSegmentName(state)
  );
  const segmentValue = useSelector((state: RootState) =>
    selectSegmentValue(state)
  );
  const selectedSegmentationType = useSelector((state: RootState) =>
    selectSegmentationType(state)
  );
  const selectedRuleAccessType = useSelector((state: RootState) =>
    selectRuleAccessType(state)
  );
  const configNameErrors = useSelector((state: RootState) =>
    selectConfigNameErrors(state)
  );
  const segmentNameErrors = useSelector((state: RootState) =>
    selectSegmentNameErrors(state)
  );

  const segmentTypeDropDownItems = useMemo(
    () =>
      Object.values(SegmentationType).map<DropDownItemType>(
        (segmentationType) => {
          return {
            key: segmentationType,
            label: segmentationType,
            isActive: selectedSegmentationType === segmentationType
          };
        }
      ),
    [selectedSegmentationType]
  );

  const ruleAccessTypeDropDownItems = useMemo(
    () =>
      Object.values(RuleAccessType).map<DropDownItemType>((ruleAccessType) => {
        return {
          key: ruleAccessType,
          label: ruleAccessType,
          isActive: selectedRuleAccessType === ruleAccessType
        };
      }),
    [selectedRuleAccessType]
  );

  const operatorDropDownItems: DropDownItemType[] = [
    {
      isActive: true,
      key: ConditionalOperator.EQUALS,
      label: 'Equals',
      pocOnly: false
    },
    {
      isActive: false,
      key: ConditionalOperator.EQUALS_CASE_INSENSITIVE,
      label: 'Equals (Case insenstive)',
      pocOnly: false
    },
    {
      isActive: false,
      key: ConditionalOperator.CONTAINS,
      label: 'Contains',
      pocOnly: false
    },
    {
      isActive: false,
      key: ConditionalOperator.DOES_NOT_CONTAIN,
      label: 'Does not contain',
      pocOnly: false
    }
  ];

  const targetTypeOptions = useSelector(selectTargetTypeOptions);

  const targetTypeDropDownItems: DropDownItemType[] = useMemo(() => {
    return targetTypeOptions.map((type) => ({
      key: type,
      label: type,
      isActive: false
    }));
  }, [targetTypeOptions]);

  const isFormValid =
    configNameErrors.length < 1 && segmentNameErrors.length < 1;

  const isRuleTargetKeyValue = (targetType: TargetType): boolean => {
    return (
      targetType === TargetType.PAYLOAD_KEY_AND_VALUE ||
      targetType ===
        TargetType.LANDING_PAGE_URL_AND_QUERY_STRING_SEGMENTS_KEY_AND_VALUE
    );
  };

  const createRules = (): SegmentationRuleDto[] => {
    const groupByKey = (array: any[], key: string) => {
      return array.reduce((result, item) => {
        const groupKey = item[key];

        if (!result[groupKey]) {
          result[groupKey] = [];
        }

        result[groupKey].push(item);

        return result;
      }, {});
    };

    const rules: SegmentationRuleDto[] = [];

    const groupedData = groupByKey(allTargets, 'rule_id');

    Object.values(groupedData).forEach((group: any) => {
      rules.push({
        priority: parseInt(group[0].rule_id),
        targets: group.map((item: RuleTarget) => {
          return {
            key: item.key ?? null,
            value: item.value,
            type: item.type,
            conditionalOperator: item.conditional_operator
          };
        })
      } as SegmentationRuleDto);
    });

    return rules;
  };

  const segmentationCategoryTitles: Record<TabKey, string> = {
    [TabKey.Forms]: 'Web',
    [TabKey.Cost]: 'Ad Platform'
  };

  const segmentationConfigTitle = segmentationCategory
    ? segmentationCategoryTitles[segmentationCategory]
    : '';

  const [updateSegmentationConfig] = useUpdateSegmentationConfigMutation();
  const [isSaving, onIsSavingChanged] = useState(false);

  const onUpdateSegmentationConfig = async () => {
    const rules = createRules();

    if (!segmentationConfigId) {
      return;
    }

    onIsSavingChanged(true);

    try {
      await updateSegmentationConfig({
        category: segmentationCategory,
        id: segmentationConfigId,
        siteId,
        ruleAccessType: selectedRuleAccessType,
        name: configName,
        updatedDateTime: new Date().toISOString(),
        updatedBy: userDetails.Email,
        segmentationType: selectedSegmentationType,
        segmentName,
        segmentValue,
        rules
      }).unwrap();

      onIsSavingChanged(false);

      dispatch(
        onSuccess({
          title: 'Segmentation Config Updated',
          description: 'The segmentation config has successfully been updated!'
        })
      );

      navigate('/settings/segmentation-config');
    } catch (error) {
      onIsSavingChanged(false);

      const errorMessages = error.data
        .map((item: any) => item.ErrorMessage)
        .join(', ');

      dispatch(
        onError({
          title: 'Segmentation Config could not be updated',
          description: errorMessages
        })
      );
    }
  };

  return (
    <TileGridContainer>
      <TileWithoutDefaultStyling className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm w-full grow basis-full">
        <section className="mx-6 my-6">
          <h2 className="text-primary-700 text-l leading-7 pb-2 mb-3 mt-2 border-b border-neutral-200">
            Edit {segmentationConfigTitle} Segment
          </h2>

          {isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="flex flex-col lg:flex-row w-full gap-6">
                <div className="flex-1 lg:w-1/3 min-h-fit border rounded border-neutral-200 mr-5 mt-5 p-6 bg-white">
                  <SegmentationRuleConfigForm
                    segmentTypeDropDownItems={segmentTypeDropDownItems}
                    ruleAccessTypeDropDownItems={ruleAccessTypeDropDownItems}
                  />
                </div>
                <div className="flex-2 lg:w-2/3 mt-5">
                  <label htmlFor="config-name" className="block mb-1 text-sm">
                    Rules
                  </label>
                  <div className="flex flex-col gap-4">
                    {allRules && allTargets ? (
                      allRules.map((rule) => (
                        <SegmentationConfigRule
                          key={`ruleid-${rule.id}`}
                          rule={rule}
                          operatorDropDownItems={operatorDropDownItems}
                          targetTypeDropDownItems={targetTypeDropDownItems}
                        />
                      ))
                    ) : (
                      <>Loading</>
                    )}
                    <div className="lg:w-144 mt-4">
                      <Button
                        theme={ButtonTheme.SECONDARY}
                        identifier="add-rule-btn"
                        isLoading={false}
                        onClick={addRule}
                      >
                        <>Add Rule</>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <Button
                  theme={ButtonTheme.SUCCESS}
                  identifier="update-segmentation-config"
                  onClick={onUpdateSegmentationConfig}
                  isLoading={isSaving}
                  isDisabled={!isFormValid}
                >
                  <span>Update</span>
                </Button>

                <div className="ml-4">
                  <NavLinkButton
                    theme={ButtonTheme.CANCEL}
                    identifier="save-segmentation-config"
                    to="/settings/segmentation-config"
                    classOverrides="px-4 py-2.5 block"
                  >
                    <span>Cancel</span>
                  </NavLinkButton>
                </div>
              </div>
            </>
          )}
        </section>
      </TileWithoutDefaultStyling>
    </TileGridContainer>
  );
};
